import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Card, Col, DatePicker, Drawer, Empty, Modal, Row, Select, Space, Spin, Table, Tag } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import _ from "lodash";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_CUSTOMER_SEGMENTS_TABLE } from "constants/FirestoreConstant";
import { formatPrice } from "functions/formatters";
import { CURRENCY } from "constants/DeliveryConstants";
import SegmentExportModal from "components/marketing/SegmentExportModal";

const { Option } = Select;

const Container = styled.div`
  padding: 1rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const SummaryCard = styled(Card)`
  height: 100%;
`;

const TrendCard = styled(Card)`
  height: 100%;
  max-height: 800px;
`;

const MetricLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
`;

const COLORS = [
  "#3366CC",
  "#DC3912",
  "#FF9900",
  "#109618",
  "#990099",
  "#0099C6",
  "#DD4477",
  "#66AA00",
  "#B82E2E",
  "#316395",
  "#994499",
];

const dateFormat = "DD-MM-YYYY";

const CustomerSegmentsDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [yesterdayMetrics, setYesterdayMetrics] = useState({});
  const [period, setPeriod] = useState("last_30_days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [marketingFocusFilter, setMarketingFocusFilter] = useState([]);
  const [shopSourceFilter, setShopSourceFilter] = useState("all");
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedSegmentData, setSelectedSegmentData] = useState(null);
  const [showSelectionModal, setShowSelectionModal] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [tempShopSourceFilter, setTempShopSourceFilter] = useState("all");
  const isInitialLoad = useRef(true);

  const getRangeForPeriod = (p) => {
    const today = dayjs();
    switch (p) {
      case "last_7_days":
        return { start: today.subtract(7, "day"), end: today };
      case "last_30_days":
        return { start: today.subtract(30, "day"), end: today };
      case "last_90_days":
        return { start: today.subtract(90, "day"), end: today };
      case "last_180_days":
        return { start: today.subtract(180, "day"), end: today };
      case "last_365_days":
        return { start: today.subtract(365, "day"), end: today };
      case "custom":
        return { start: startDate, end: endDate };
      default:
        return { start: today.subtract(30, "day"), end: today };
    }
  };

  const fetchData = async (sourceFilter = null) => {
    setLoading(true);
    const filterToUse = sourceFilter !== null ? sourceFilter : shopSourceFilter;
    try {
      const databases = [];
      if (filterToUse === "all" || filterToUse === "VCE") {
        databases.push({ db: vceFirestore, source: "VCE" });
      }
      if (filterToUse === "all" || filterToUse === "HVO") {
        databases.push({ db: hvoFirestore, source: "HVO" });
      }

      const allSegments = [];
      const allCounts = {};
      const allTrendData = {};
      const allYesterdayMetrics = {};
      const customersArray = [];

      for (const { db, source } of databases) {
        const segmentsSnap = await db.collection(FIRESTORE_CUSTOMER_SEGMENTS_TABLE).get();
        const segs = segmentsSnap.docs.map((d) => ({ id: d.id, ...d.data(), shopSource: source }));
        allSegments.push(...segs);

        const { start, end } = getRangeForPeriod(period === "custom" ? "custom" : period);
        const useStart = start || dayjs().subtract(30, "day");
        const useEnd = end || dayjs();
        const dateRange = [];
        for (let d = useStart; d.isBefore(useEnd.add(1, "day")); d = d.add(1, "day")) {
          dateRange.push(d.format(dateFormat));
        }

        const perSegmentHistory = {};
        const yesterdayPerSegment = {};

        for (const seg of segs) {
          const histSnap = await db
            .collection(FIRESTORE_CUSTOMER_SEGMENTS_TABLE)
            .doc(seg.id)
            .collection("history")
            .get();
          const byDate = {};
          histSnap.docs.forEach((doc) => {
            const idDate = doc.id;
            const data = doc.data();
            const users = Array.isArray(data.users) ? data.users : [];
            byDate[idDate] = users.length;

            if (idDate === dayjs().format(dateFormat)) {
              yesterdayPerSegment[`${source}_${seg.id}`] = data.metrics || null;
              const key = `${source}_${seg.id}`;
              allCounts[key] = users.length;

              users.forEach((userId) => {
                customersArray.push({
                  id: userId,
                  segment: seg.id,
                  segmentName: seg.name || seg.id,
                  shopSource: source,
                });
              });
            }
          });
          perSegmentHistory[`${source}_${seg.id}`] = byDate;
        }

        Object.assign(allYesterdayMetrics, yesterdayPerSegment);

        if (!allTrendData[source]) {
          allTrendData[source] = dateRange.map((dateStr) => {
            const entry = { date: dateStr };
            segs.forEach((seg) => {
              entry[`${source}_${seg.name || seg.id}`] = perSegmentHistory[`${source}_${seg.id}`]?.[dateStr] || 0;
            });
            return entry;
          });
        }
      }

      setSegments(allSegments);
      setSegmentCounts(allCounts);
      setTrendData(Object.values(allTrendData));
      setYesterdayMetrics(allYesterdayMetrics);
      setAllCustomers(customersArray);

      const combinedTrend = Object.values(allTrendData).reduce((acc, data) => {
        if (acc.length === 0) return data;
        return acc.map((entry, idx) => ({ ...entry, ...data[idx] }));
      }, []);
      setTrendData(combinedTrend);

      const sortedByCount = _.orderBy(
        allSegments.map((s) => ({
          seg: s,
          count: allCounts[`${s.shopSource}_${s.id}`] || allCounts[`${s.shopSource}_${s.name}`] || 0,
        })),
        [(x) => x.count],
        ["desc"],
      );
      setSelectedSegments(sortedByCount.map((x) => `${x.seg.shopSource}_${x.seg.id}`));
      setDataLoaded(true);
    } catch (e) {
      console.error("Failed to load customer segments dashboard", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dataLoaded && !isInitialLoad.current) {
      fetchData();
    }
  }, [period, startDate, endDate, shopSourceFilter]);

  const handleLoadData = async () => {
    setShowSelectionModal(false);
    setShopSourceFilter(tempShopSourceFilter);
    setDataLoaded(true);
    isInitialLoad.current = false;

    // Gebruik de tempShopSourceFilter direct om race conditions te voorkomen
    await fetchData(tempShopSourceFilter);
  };

  const summary = useMemo(() => {
    const total = _.sum(Object.values(segmentCounts));
    const byName = {};
    segments.forEach((s) => {
      const key = `${s.shopSource}_${s.name || s.id}`;
      const val = segmentCounts[`${s.shopSource}_${s.id}`] ?? segmentCounts[`${s.shopSource}_${s.name}`] ?? 0;
      byName[key] = val;
    });
    return { total, byName };
  }, [segmentCounts, segments]);

  const filteredSegments = useMemo(() => {
    let list = segments;
    if (marketingFocusFilter.length > 0)
      list = list.filter(
        (s) => Array.isArray(s.marketingFocus) && s.marketingFocus.some((f) => marketingFocusFilter.includes(f)),
      );
    if (selectedSegments.length > 0) list = list.filter((s) => selectedSegments.includes(`${s.shopSource}_${s.id}`));
    return list;
  }, [segments, marketingFocusFilter, selectedSegments]);

  const allMarketingFocusOptions = useMemo(() => {
    const set = new Set();
    segments.forEach((s) => (s.marketingFocus || []).forEach((f) => set.add(f)));
    return Array.from(set);
  }, [segments]);

  const segmentTableData = useMemo(() => {
    return filteredSegments.map((s) => {
      const key = `${s.shopSource}_${s.id}`;
      const metrics = yesterdayMetrics[key];
      const count = segmentCounts[`${s.shopSource}_${s.id}`] ?? segmentCounts[`${s.shopSource}_${s.name}`] ?? 0;
      return {
        key,
        shopSource: s.shopSource,
        name: s.name || s.id,
        count,
        newCustomer: s.newCustomer ? "Yes" : "No",
        marketingFocus: s.marketingFocus || [],
        rfmScores: (s.rfmScores || []).join(", "),
        ...(metrics?.analysisPeriod
          ? {
              analysisPeriod: metrics.analysisPeriod,
              averageDaysSinceLastOrder: metrics.averageDaysSinceLastOrder,
              averageOrderValue: metrics.averageOrderValue,
              averageOrdersInPeriod: metrics.averageOrdersInPeriod,
              averageOrdersPerMonth: metrics.averageOrdersPerMonth,
              ordersInPeriod: metrics.ordersInPeriod,
              totalSpentInPeriod: metrics.totalSpentInPeriod,
            }
          : {}),
      };
    });
  }, [filteredSegments, yesterdayMetrics, segmentCounts]);

  const columns = [
    {
      title: t("shop"),
      dataIndex: "shopSource",
      key: "shopSource",
      render: (shopSource) => <Tag color={shopSource === "VCE" ? "cyan" : "magenta"}>{shopSource}</Tag>,
      width: 80,
    },
    {
      title: t("segment"),
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <a
          onClick={() => {
            setSelectedSegmentData(record);
            setDrawerVisible(true);
          }}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("count"),
      dataIndex: "count",
      key: "count",
      sorter: (a, b) => a.count - b.count,
    },
    { title: t("analysis_period"), dataIndex: "analysisPeriod", key: "analysisPeriod" },
    {
      title: t("avg_days_since_last_order"),
      dataIndex: "averageDaysSinceLastOrder",
      key: "averageDaysSinceLastOrder",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => (a.averageDaysSinceLastOrder || 0) - (b.averageDaysSinceLastOrder || 0),
    },
    {
      title: t("avg_order_value"),
      dataIndex: "averageOrderValue",
      key: "averageOrderValue",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val ? CURRENCY + formatPrice(val) : "-"}</span>,
      sorter: (a, b) => (a.averageOrderValue || 0) - (b.averageOrderValue || 0),
    },
    {
      title: t("avg_orders_in_period"),
      dataIndex: "averageOrdersInPeriod",
      key: "averageOrdersInPeriod",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => (a.averageOrdersInPeriod || 0) - (b.averageOrdersInPeriod || 0),
    },
    {
      title: t("avg_orders_per_month"),
      dataIndex: "averageOrdersPerMonth",
      key: "averageOrdersPerMonth",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => (a.averageOrdersPerMonth || 0) - (b.averageOrdersPerMonth || 0),
    },
    {
      title: t("orders_in_period"),
      dataIndex: "ordersInPeriod",
      key: "ordersInPeriod",
      sorter: (a, b) => (a.ordersInPeriod || 0) - (b.ordersInPeriod || 0),
    },
    {
      title: t("total_spent_in_period"),
      dataIndex: "totalSpentInPeriod",
      key: "totalSpentInPeriod",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val ? CURRENCY + formatPrice(val) : "-"}</span>,
      sorter: (a, b) => (a.totalSpentInPeriod || 0) - (b.totalSpentInPeriod || 0),
    },
  ];

  const trendSeries = useMemo(() => {
    const idToName = _.fromPairs(
      segments.map((s) => [`${s.shopSource}_${s.id}`, `${s.shopSource} - ${s.name || s.id}`]),
    );
    const ids = selectedSegments.length > 0 ? selectedSegments : filteredSegments.map((s) => `${s.shopSource}_${s.id}`);
    return ids.map((id, i) => ({ id, name: idToName[id], color: COLORS[i % COLORS.length] }));
  }, [selectedSegments, segments, filteredSegments]);

  const uniqueSegmentNames = useMemo(() => {
    return [...new Set(segments.map((s) => s.name || s.id))];
  }, [segments]);

  return (
    <Container>
      <Modal
        title={t("select_shop_source")}
        open={showSelectionModal}
        onOk={handleLoadData}
        onCancel={() => setShowSelectionModal(false)}
        okText={t("load_data") || "Laad Data"}
        cancelText={t("cancel") || "Annuleer"}
        closable={false}
        maskClosable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <p>{t("select_shop_source_description") || "Selecteer welke data je wilt laden:"}</p>
          <Select
            value={tempShopSourceFilter}
            onChange={(val) => setTempShopSourceFilter(val)}
            style={{ width: "100%" }}
          >
            <Option value="all">{t("all") || "Alles"} (VCE + HVO)</Option>
            <Option value="VCE">VCE</Option>
            <Option value="HVO">HVO</Option>
          </Select>
        </div>
      </Modal>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title>{t("customer_segments")}</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => setExportModalVisible(true)}
            disabled={loading || !dataLoaded}
          >
            {t("export_segment")}
          </Button>
        </Col>
      </Row>

      {!dataLoaded ? (
        <Empty description={t("select_data_to_load") || "Selecteer eerst welke data je wilt laden"} />
      ) : loading ? (
        <Spin tip={t("loading_data") || "Data laden..."} />
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={6}>
                <SectionTitle>{t("shop_source")}</SectionTitle>
                <Select
                  value={shopSourceFilter}
                  onChange={(val) => setShopSourceFilter(val)}
                  style={{ width: "100%" }}
                  disabled={loading}
                >
                  <Option value="all">{t("all")}</Option>
                  <Option value="VCE">VCE</Option>
                  <Option value="HVO">HVO</Option>
                </Select>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <SectionTitle>{t("date_range")}</SectionTitle>
                <Select value={period} onChange={(val) => setPeriod(val)} style={{ width: "100%" }}>
                  <Option value="last_7_days">{t("last_7_days")}</Option>
                  <Option value="last_30_days">{t("last_30_days")}</Option>
                  <Option value="last_90_days">{t("last_90_days")}</Option>
                  <Option value="last_180_days">{t("last_180_days")}</Option>
                  <Option value="last_365_days">{t("last_365_days")}</Option>
                  <Option value="custom">{t("custom_range")}</Option>
                </Select>
                {period === "custom" && (
                  <DatePicker.RangePicker
                    value={[startDate, endDate]}
                    onChange={(vals) => {
                      setStartDate(vals?.[0] || null);
                      setEndDate(vals?.[1] || null);
                    }}
                    format={dateFormat}
                    allowClear
                    style={{ width: "100%", marginTop: 8 }}
                  />
                )}
              </Col>
              <Col xs={24} md={12} lg={6}>
                <SectionTitle>{t("marketing_focus_filter")}</SectionTitle>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("marketing_focus_filter")}
                  value={marketingFocusFilter}
                  onChange={setMarketingFocusFilter}
                  options={allMarketingFocusOptions.map((f) => ({ label: f, value: f }))}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={12} lg={6}>
                <SectionTitle>{t("segments_filter")}</SectionTitle>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("segments_filter")}
                  value={selectedSegments}
                  onChange={setSelectedSegments}
                  options={segments.map((s) => ({
                    value: `${s.shopSource}_${s.id}`,
                    label: `${s.shopSource} - ${s.name || s.id}`,
                  }))}
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          </Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <SummaryCard>
                <MetricLabel>{t("total_customers")}</MetricLabel>
                <MetricValue>{summary.total}</MetricValue>
              </SummaryCard>
            </Col>
            {filteredSegments.slice(0, 3).map((s, idx) => (
              <Col xs={24} sm={12} md={6} key={`${s.shopSource}_${s.id}`}>
                <SummaryCard>
                  <MetricLabel>
                    {s.shopSource} - {s.name || s.id}
                  </MetricLabel>
                  <MetricValue>{summary.byName[`${s.shopSource}_${s.name || s.id}`] || 0}</MetricValue>
                </SummaryCard>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <TrendCard title={<SectionTitle style={{ margin: 0 }}>{t("segment_size_trends")}</SectionTitle>}>
                {trendData.length === 0 ? (
                  <Empty />
                ) : (
                  <div style={{ width: "100%", height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip />
                        {trendSeries.map((s) => {
                          const segment = segments.find((x) => `${x.shopSource}_${x.id}` === s.id);
                          const dataKey = segment ? `${segment.shopSource}_${segment.name || segment.id}` : s.id;
                          return <Line key={s.id} type="monotone" dataKey={dataKey} stroke={s.color} dot={false} />;
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </TrendCard>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card>
                <SectionTitle>{t("segment_details_today")}</SectionTitle>
                <Table
                  size="small"
                  dataSource={segmentTableData}
                  columns={columns}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1400 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <SegmentExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        customers={allCustomers}
        segments={uniqueSegmentNames}
      />

      <Drawer
        title={selectedSegmentData?.name}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={500}
      >
        {selectedSegmentData && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <SectionTitle>{t("marketing_focus")}</SectionTitle>
              <Space wrap>
                {(selectedSegmentData.marketingFocus || []).length > 0 ? (
                  selectedSegmentData.marketingFocus.map((v) => <Tag key={v}>{v}</Tag>)
                ) : (
                  <span>-</span>
                )}
              </Space>
            </div>
            <div>
              <SectionTitle>{t("rfm_scores")}</SectionTitle>
              <div>{selectedSegmentData.rfmScores || "-"}</div>
            </div>
          </Space>
        )}
      </Drawer>
    </Container>
  );
};

export default CustomerSegmentsDashboard;
